import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/apiClient';

// API base URL - configurable via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function Alerts() {
  // Notification Center state
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('ALL');
  const [premiumPortfolioSettings, setPremiumPortfolioSettings] = useState([]);
  const [premiumPublication, setPremiumPublication] = useState(null);
  const [premiumPortfolioSettingsError, setPremiumPortfolioSettingsError] = useState('');
  
  // Alert Preferences state
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    min_investment_score_threshold: 50,
    review_rank_threshold: 10,
    new_opportunity_min_score: 50,
    allow_overbought_opportunities: true
  });
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferencesMessage, setPreferencesMessage] = useState('');

  // User info state
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [error, setError] = useState('');

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/alerts`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setNotifications(data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      // Don't set error state - notifications are optional
    } finally {
      setLoadingNotifications(false);
    }
  };

  const fetchPremiumPortfolioSettings = async () => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/premium-portfolio-alert-settings`, { method: 'GET' });
      if (!response.ok) throw new Error('Unable to load Premium portfolio alert settings.');
      const data = await response.json();
      setPremiumPortfolioSettings(Array.isArray(data.settings) ? data.settings : []);
      setPremiumPublication(
        data.active_publication_id
          ? { id: data.active_publication_id, anchorDate: data.active_anchor_date }
          : null
      );
      setPremiumPortfolioSettingsError('');
    } catch (err) {
      setPremiumPortfolioSettings([]);
      setPremiumPublication(null);
      setPremiumPortfolioSettingsError(err.message);
    }
  };

  const fetchAlertPreferences = async () => {
    setLoadingPreferences(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/user/alert-preferences`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setPreferences({
        min_investment_score_threshold: data.min_investment_score_threshold || 50,
        review_rank_threshold: data.review_rank_threshold || 10,
        new_opportunity_min_score: data.new_opportunity_min_score || 50,
        allow_overbought_opportunities: data.allow_overbought_opportunities !== undefined ? data.allow_overbought_opportunities : true
      });
    } catch (err) {
      console.error('Failed to fetch alert preferences:', err);
      // Don't set error state - use defaults
    } finally {
      setLoadingPreferences(false);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/me`, {
          method: 'GET',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        setUserInfo(data);
        
        // Only fetch Pro-only endpoints if user has Pro or Unlimited plan
        if (data.plan_type === 'pro' || data.plan_type === 'unlimited') {
          fetchNotifications();
          fetchAlertPreferences();
        }
        if (data.plan_type === 'unlimited') {
          fetchPremiumPortfolioSettings();
        }
      } catch (err) {
        console.error('Failed to fetch user info:', err);
        // Don't set error state - keep page usable
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []);

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    setPreferencesMessage('');
    setError('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/user/alert-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setPreferences({
        min_investment_score_threshold: data.min_investment_score_threshold || 50,
        review_rank_threshold: data.review_rank_threshold || 10,
        new_opportunity_min_score: data.new_opportunity_min_score || 50,
        allow_overbought_opportunities: data.allow_overbought_opportunities !== undefined ? data.allow_overbought_opportunities : true
      });
      setPreferencesMessage('✓ General Alert preferences saved for the next scheduled weekly check.');
      
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setPreferencesMessage('');
      }, 3000);
    } catch (err) {
      setError(`Failed to save preferences: ${err.message}`);
    } finally {
      setSavingPreferences(false);
    }
  };

  const getFilteredNotifications = () => {
    // Only show ACTIVE and IGNORED alerts - hide RESOLVED and other inactive statuses
    const activeAndIgnoredAlerts = notifications.filter(alert =>
      alert.status === 'ACTIVE' || alert.status === 'IGNORED'
    );
    
    if (notificationFilter === 'ALL') {
      return activeAndIgnoredAlerts;
    }
    return activeAndIgnoredAlerts.filter(alert => alert.status === notificationFilter);
  };

  const getStatusDisplayLabel = (status) => {
    if (status === 'ACTIVE') {
      return 'ACTIVE';
    }
    if (status === 'IGNORED') {
      return 'ACKNOWLEDGED';
    }
    // Return null for any other status - should not display
    return null;
  };

  const getAlertTypeLabel = (alertType) => {
    switch (alertType) {
      case 'low_investment_score':
        return 'Low Investment Score';
      case 'review_position':
        return 'Outside Preferred Rank';
      case 'missing_from_scan':
        return 'Outside Current Universe';
      case 'new_opportunity':
        return 'New Opportunity';
      case 'strategy_top_n_entry':
        return 'Stock Entered Top N';
      case 'strategy_top_n_exit':
        return 'Stock Left Top N';
      default:
        return alertType || 'Alert';
    }
  };

  const isPremiumCustomAlert = (alert) => (
    Boolean(alert?.validation_lab_publication_id)
      && ['strategy_top_n_entry', 'strategy_top_n_exit'].includes(alert.alert_type)
  );

  return (
    <div className="alerts-page authenticated-page">
      <div className="alerts-container authenticated-page-content">
        {/* Page Header */}
        <div className="alerts-header">
          <div className="alerts-header-content">
            <h1 className="alerts-title">Alerts / Notification Center</h1>
            <p className="alerts-subtitle">
              Manage account-level Weekly Digest and Premium Portfolio Alerts, and view notifications.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alerts-error">
            <svg className="alerts-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Alert Preferences Panel - Pro Only */}
        {!loadingUserInfo && userInfo && userInfo.plan_type === 'free' ? (
          <div className="alerts-upgrade-card">
            <div className="alerts-upgrade-icon-wrapper">
              <svg className="alerts-upgrade-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <div className="alerts-upgrade-content">
              <h2 className="alerts-upgrade-title">Portfolio Alert Preferences</h2>
              <p className="alerts-upgrade-text">
                Alerts and Notification Center are available on Pro and Premium plans.
              </p>
            </div>
          </div>
        ) : (
          <div className="alerts-preferences-section">
            <div className="alerts-preferences-header">
              <h2 className="alerts-preferences-title">Weekly Digest</h2>
              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="alerts-btn-toggle"
              >
                {showPreferences ? 'Hide Settings' : 'Show Settings'}
              </button>
            </div>

            {showPreferences && (
              <div className="alerts-preferences-content">
                <p className="alerts-preferences-description">
                  Your settings apply across all your portfolios. Each week, we check holdings and new opportunities against the latest stock ranking and email you a summary.
                </p>

                {loadingPreferences && <p className="alerts-loading-text">Loading preferences...</p>}

                {!loadingPreferences && (
                  <div className="alerts-preferences-form">
                    {/* Min Investment Score Threshold */}
                    <div className="alerts-preference-row">
                      <label className="alerts-preference-label">
                        Alert me when a holding falls below this Investment Score:
                      </label>
                      <div className="alerts-preference-input-group">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={preferences.min_investment_score_threshold}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            min_investment_score_threshold: parseFloat(e.target.value)
                          })}
                          className="alerts-preference-input"
                        />
                        <span className="alerts-preference-hint">
                          (0-100, default: 50)
                        </span>
                      </div>
                      <p className="alerts-preference-help">
                        You'll be notified when any portfolio holding's Investment Score drops below this threshold.
                      </p>
                    </div>

                    {/* Review Rank Threshold */}
                    <div className="alerts-preference-row">
                      <label className="alerts-preference-label">
                        Alert me when a holding falls outside my preferred rank range:
                      </label>
                      <div className="alerts-preference-input-group">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={preferences.review_rank_threshold}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            review_rank_threshold: parseInt(e.target.value)
                          })}
                          className="alerts-preference-input"
                        />
                        <span className="alerts-preference-hint">
                          (default: 10)
                        </span>
                      </div>
                      <p className="alerts-preference-help">
                        This is a relative ranking alert from the current global operational snapshot. A holding may still have a good Investment Score while falling outside your preferred rank range.
                      </p>
                    </div>

                    {/* New Opportunity Min Score */}
                    <div className="alerts-preference-row">
                      <label className="alerts-preference-label">
                        Show new opportunities only above this Investment Score:
                      </label>
                      <div className="alerts-preference-input-group">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={preferences.new_opportunity_min_score}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            new_opportunity_min_score: parseFloat(e.target.value)
                          })}
                          className="alerts-preference-input"
                        />
                        <span className="alerts-preference-hint">
                          (0-100, default: 50)
                        </span>
                      </div>
                      <p className="alerts-preference-help">
                        Only stocks with Investment Scores above this threshold will appear as new opportunities.
                      </p>
                    </div>

                    {/* Allow Overbought Opportunities */}
                    <div className="alerts-preference-row">
                      <label className="alerts-preference-checkbox-label">
                        <input
                          type="checkbox"
                          checked={preferences.allow_overbought_opportunities}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            allow_overbought_opportunities: e.target.checked
                          })}
                          className="alerts-preference-checkbox"
                        />
                        Include overbought stocks in opportunities
                      </label>
                      <p className="alerts-preference-help alerts-preference-help-indented">
                        When unchecked, stocks with "Overbought" entry timing will be excluded from new opportunities.
                      </p>
                    </div>

                    {/* Save Button */}
                    <div className="alerts-preference-actions">
                      <button
                        onClick={handleSavePreferences}
                        disabled={savingPreferences}
                        className="alerts-btn-save"
                      >
                        {savingPreferences ? 'Saving...' : 'Save Preferences'}
                      </button>
                      
                      <p className="alerts-preference-help" role="status">
                        Saving updates the next scheduled weekly check; it does not send an alert immediately.
                      </p>
                      {preferencesMessage && (
                        <span className="alerts-save-success">
                          {preferencesMessage}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {userInfo?.plan_type === 'unlimited' && (
          <section className="alerts-preferences-section" aria-label="Premium portfolio alert settings">
            <div className="alerts-preferences-header">
              <h2 className="alerts-preferences-title">Portfolio Alerts</h2>
            </div>
            <p className="alerts-preferences-description">
              Each month, we compare this portfolio with the highest-ranked stocks matching your filters—the Top N. You’ll receive an email when a stock in your portfolio leaves the Top N and a replacement enters. Rankings update monthly as company fundamentals change. Saving settings won’t send an email immediately.
            </p>
            {premiumPortfolioSettingsError && <p className="alerts-error" role="alert">{premiumPortfolioSettingsError}</p>}
            {premiumPublication && (
              <p className="alerts-preference-help">
                Latest monthly ranking used for alerts: {premiumPublication.anchorDate || 'Date not recorded'} (publication {premiumPublication.id})
              </p>
            )}
            {!premiumPortfolioSettingsError && premiumPortfolioSettings.length === 0 && (
              <p className="alerts-preference-help">No portfolio-specific Premium alert settings have been saved yet.</p>
            )}
            {premiumPortfolioSettings.map((settings) => (
              <div className="alerts-preference-row" key={settings.portfolio_id}>
                <strong>{settings.portfolio_name}</strong>
                <ul className="alerts-preference-help" style={{ marginTop: '6px' }}>
                  <li>Country: {settings.country || 'All Countries'}</li>
                  <li>Universe: {settings.universe_code}</li>
                  <li>Sector: {settings.sector || 'All Sectors'}</li>
                  <li>Industry: {settings.industry || 'All Industries'}</li>
                  <li>Top-N rank cutoff: {settings.top_n}</li>
                  <li>Investor Score tolerance: {settings.investor_score_tolerance}</li>
                </ul>
              </div>
            ))}
          </section>
        )}

        {/* Notification Center - Pro Only */}
        {!loadingUserInfo && userInfo && userInfo.plan_type === 'free' ? (
          <div className="alerts-upgrade-card">
            <div className="alerts-upgrade-icon-wrapper">
              <svg className="alerts-upgrade-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </div>
            <div className="alerts-upgrade-content">
              <h2 className="alerts-upgrade-title">Notification Center</h2>
              <p className="alerts-upgrade-text">
                Alerts and Notification Center are available on Pro and Premium plans.
              </p>
            </div>
          </div>
        ) : (
          <div className="alerts-notifications-section">
            <div className="alerts-notifications-header">
              <h2 className="alerts-notifications-title">Notification Center</h2>
            </div>

            {/* Filter Buttons */}
            <div className="alerts-filter-buttons">
              {['ALL', 'ACTIVE', 'ACKNOWLEDGED'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setNotificationFilter(filter === 'ACKNOWLEDGED' ? 'IGNORED' : filter)}
                  className={`alerts-filter-btn ${notificationFilter === (filter === 'ACKNOWLEDGED' ? 'IGNORED' : filter) ? 'active' : ''}`}
                >
                  {filter}
                  {filter !== 'ALL' && (
                    <span className="alerts-filter-count">
                      ({notifications.filter(a => a.status === (filter === 'ACKNOWLEDGED' ? 'IGNORED' : filter)).length})
                    </span>
                  )}
                  {filter === 'ALL' && (
                    <span className="alerts-filter-count">
                      ({notifications.filter(a => a.status === 'ACTIVE' || a.status === 'IGNORED').length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Notifications List */}
            {loadingNotifications && <p className="alerts-loading-text">Loading notifications...</p>}
          
            {!loadingNotifications && notifications.length === 0 && (
              <div className="alerts-empty-state">
                <svg className="alerts-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <p className="alerts-empty-text">
                  No notifications - You're all caught up!
                </p>
              </div>
            )}

            {!loadingNotifications && getFilteredNotifications().length === 0 && notifications.length > 0 && (
              <div className="alerts-no-results">
                <p className="alerts-no-results-text">
                  No notifications with status: {notificationFilter}
                </p>
              </div>
            )}

            {!loadingNotifications && getFilteredNotifications().length > 0 && (
              <div className="alerts-list">
                {getFilteredNotifications().map((alert, index) => {
                  const statusLabel = getStatusDisplayLabel(alert.status);
                  
                  return (
                    <div
                      key={alert.id || index}
                      className={`alerts-card ${alert.status === 'ACTIVE' ? 'alerts-card-active' : 'alerts-card-acknowledged'}`}
                    >
                      <div className="alerts-card-header">
                        <div className="alerts-card-badges">
                          {statusLabel && (
                            <span className={`alerts-status-badge ${alert.status === 'ACTIVE' ? 'alerts-status-active' : 'alerts-status-acknowledged'}`}>
                              {statusLabel}
                            </span>
                          )}
                          <span className="alerts-type-badge">
                            {isPremiumCustomAlert(alert) ? 'Portfolio Alerts' : 'Weekly Digest'}
                            {' — '}
                            {getAlertTypeLabel(alert.alert_type)}
                          </span>
                        </div>
                      </div>

                      <div className="alerts-card-ticker">
                        {alert.ticker}
                      </div>

                      {alert.portfolio_name && (
                        <p className="alerts-preference-help">Portfolio: {alert.portfolio_name}</p>
                      )}
                      {alert.rule_context && (
                        <div className="alerts-preference-help" aria-label="Alert context recorded at creation">
                          <strong>Alert context recorded at creation</strong>
                          <ul style={{ marginTop: '6px' }}>
                            {alert.validation_lab_publication_id && <li>Validation Lab publication: {alert.validation_lab_publication_id}</li>}
                            {alert.rule_context.country !== undefined && <li>Country: {alert.rule_context.country || 'All Countries'}</li>}
                            {alert.rule_context.universe_code !== undefined && <li>Universe: {alert.rule_context.universe_code}</li>}
                            {alert.rule_context.sector !== undefined && <li>Sector: {alert.rule_context.sector || 'All Sectors'}</li>}
                            {alert.rule_context.industry !== undefined && <li>Industry: {alert.rule_context.industry || 'All Industries'}</li>}
                            {alert.rule_context.top_n !== undefined && <li>Top-N rank cutoff: {alert.rule_context.top_n}</li>}
                            {alert.rule_context.investor_score_tolerance !== undefined && <li>Investor Score tolerance: {alert.rule_context.investor_score_tolerance}</li>}
                            {alert.rule_context.score_difference !== undefined && <li>Investor Score difference: {alert.rule_context.score_difference}</li>}
                          </ul>
                        </div>
                      )}
                      {isPremiumCustomAlert(alert) && (
                        <p className="alerts-preference-help" style={{ fontSize: 'var(--font-size-sm)' }}>Alerts are issued monthly because the fundamentals behind the rankings are updated monthly. After each update, you’ll be alerted if a stock leaves your filtered Top‑N and a higher-scoring stock enters by more than your tolerance.</p>
                      )}

                      <p className="alerts-card-message">
                        {alert.message || 'No message available'}
                      </p>

                      <div className="alerts-card-footer">
                        <span className="alerts-card-date">
                          {alert.created_at 
                            ? new Date(alert.created_at).toLocaleString()
                            : 'Unknown date'
                          }
                        </span>
                        {alert.updated_at && alert.updated_at !== alert.created_at && (
                          <span className="alerts-card-updated">
                            Updated: {new Date(alert.updated_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Alerts;
